import { utils } from "@defuse-protocol/internal-utils"
import { base58, base64 } from "@scure/base"
import { nearClient } from "@src/components/DefuseSDK/constants/nearClient"
import type { IntentsUserId } from "@src/components/DefuseSDK/types/intentsUserId"
import * as v from "valibot"
import { getUnstakeMessageHash } from "./protocol"
import type {
  LstCurrentEpochEstimate,
  LstUserWithdrawalRequest,
  LstWithdrawalTranche,
} from "./protocol"

const RatioSchema = v.object({
  numerator: v.string(),
  denominator: v.string(),
})

const StorageBalanceSchema = v.nullable(
  v.object({
    total: v.string(),
    available: v.string(),
  })
)

const NearTokenSchema = v.pipe(
  v.string(),
  v.transform((amount) => BigInt(amount))
)

const EpochSchema = v.pipe(
  v.union([v.number(), v.string()]),
  v.transform((epoch) => Number(epoch))
)

const TrancheSchema = v.pipe(
  v.union([
    v.looseObject({
      amount: NearTokenSchema,
      unstake_epoch: EpochSchema,
    }),
    v.looseObject({
      withdrawal_amount: NearTokenSchema,
      unstake_epoch: EpochSchema,
    }),
  ]),
  v.transform((tranche): WithdrawalTranche => {
    const amount =
      "amount" in tranche ? tranche.amount : tranche.withdrawal_amount
    return {
      amount: amount as bigint,
      unstakeEpoch: tranche.unstake_epoch,
    }
  })
)

const MaybeTranchesSchema = v.nullable(v.array(TrancheSchema))

export interface PoolSnapshot {
  /** LST -> NEAR ratio. Multiply LST yocto x numerator / denominator. */
  rate: { numerator: bigint; denominator: bigint }
}

export type WithdrawalTranche = LstWithdrawalTranche
export type UserWithdrawalRequest = LstUserWithdrawalRequest
export type CurrentEpochEstimate = LstCurrentEpochEstimate

interface EpochValidatorInfoWithHeight {
  epoch_height: number
  epoch_start_height: number
}

const ESTIMATED_NEAR_EPOCH_DURATION_MS = 12 * 60 * 60 * 1_000

async function callView<TSchema extends v.GenericSchema>(
  contractId: string,
  methodName: string,
  schema: TSchema,
  args: Record<string, unknown> = {}
): Promise<v.InferOutput<TSchema>> {
  const response = await nearClient.query({
    request_type: "call_function",
    method_name: methodName,
    account_id: contractId,
    args_base64: encodeJsonArgs(args),
    finality: "optimistic",
  })
  return utils.decodeQueryResult(
    response,
    schema as v.GenericSchema<unknown, v.InferOutput<TSchema>>
  )
}

function encodeJsonArgs(args: Record<string, unknown>): string {
  return base64.encode(new TextEncoder().encode(JSON.stringify(args)))
}

function hashToBytes(hash: string): number[] {
  return Array.from(base58.decode(hash))
}

function getExchangeRate(contractId: string) {
  return callView(contractId, "get_exchange_rate", RatioSchema)
}

async function getCurrentEpochEstimate(): Promise<CurrentEpochEstimate> {
  const validators = (await nearClient.validators(
    null
  )) as unknown as EpochValidatorInfoWithHeight
  const [epochStartBlock, latestBlock] = await Promise.all([
    nearClient.block({ blockId: validators.epoch_start_height }),
    nearClient.block({ finality: "optimistic" }),
  ])

  return {
    epochHeight: validators.epoch_height,
    epochStartedAtMs: timestampNsToMs(epochStartBlock.header.timestamp_nanosec),
    estimatedEpochDurationMs: ESTIMATED_NEAR_EPOCH_DURATION_MS,
    nowMs: timestampNsToMs(latestBlock.header.timestamp_nanosec),
  }
}

async function isFtStorageRegistered(
  contractId: string,
  accountId: string
): Promise<boolean> {
  const storageBalance = await callView(
    contractId,
    "storage_balance_of",
    StorageBalanceSchema,
    { account_id: accountId }
  )
  return storageBalance != null
}

function getWithdrawalRequestTranches(
  contractId: string,
  hash: string
): Promise<WithdrawalTranche[] | null> {
  return callView(
    contractId,
    "get_withdrawal_request_tranches",
    MaybeTranchesSchema,
    {
      hash: hashToBytes(hash),
    }
  )
}

async function getPoolSnapshot(contractId: string): Promise<PoolSnapshot> {
  const rate = await getExchangeRate(contractId)

  return {
    rate: {
      numerator: BigInt(rate.numerator),
      denominator: BigInt(rate.denominator),
    },
  }
}

async function getUserWithdrawalRequest(
  contractId: string,
  userId: IntentsUserId
): Promise<UserWithdrawalRequest> {
  const hash = getUnstakeMessageHash(userId)
  const tranches = await getWithdrawalRequestTranches(contractId, hash)

  if (tranches == null || tranches.length === 0) {
    return {
      hash,
      tranches: [],
      totalAmount: 0n,
    }
  }

  return {
    hash,
    tranches,
    totalAmount: sumTranches(tranches),
  }
}

async function waitForUserWithdrawalRequest(
  contractId: string,
  userId: IntentsUserId,
  {
    maxAttempts = 12,
    delayMs = 5_000,
    signal,
    previousTotalAmount = 0n,
  }: {
    maxAttempts?: number
    delayMs?: number
    signal?: AbortSignal
    // Total queued before this unstake. We wait for the queue to grow beyond it
    // so a request that was already pending can't satisfy the confirmation.
    previousTotalAmount?: bigint
  } = {}
): Promise<UserWithdrawalRequest> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    signal?.throwIfAborted()

    const request = await getUserWithdrawalRequest(contractId, userId)
    if (request.totalAmount > previousTotalAmount) return request

    if (attempt < maxAttempts - 1) {
      await sleep(delayMs, signal)
    }
  }

  throw new Error("LST withdrawal request not found")
}

export const nearStakingContract = {
  getCurrentEpochEstimate,
  getPoolSnapshot,
  getUserWithdrawalRequest,
  isFtStorageRegistered,
  waitForUserWithdrawalRequest,
}

function sumTranches(tranches: WithdrawalTranche[]): bigint {
  return tranches.reduce((total, tranche) => total + tranche.amount, 0n)
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal == null) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      signal.removeEventListener("abort", onAbort)
      resolve()
    }, ms)

    const onAbort = () => {
      clearTimeout(timeout)
      reject(
        signal.reason instanceof Error ? signal.reason : new Error("Aborted")
      )
    }

    if (signal.aborted) {
      onAbort()
      return
    }

    signal.addEventListener("abort", onAbort, { once: true })
  })
}

function timestampNsToMs(timestampNs: string): number {
  return Number(BigInt(timestampNs) / 1_000_000n)
}
